'use client';

import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Maximize2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import type { CoverageTimelineView } from '@/types/coverage-timeline';
import { TimelineChart, TimelineLegend, timelineDomain } from './MissionCoverageTimeline';

/**
 * THE SAME CHART, BEFORE ANYTHING IS ATTACHED (PR 3, S3).
 *
 * One picture, used twice - the idea the whole redesign turns on. `MissionCoverageTimeline` is the
 * status board an operator reads once coverage is running; this is the simulator they read while
 * deciding what to attach. Both draw with `TimelineChart`, so there is one implementation of lane
 * height, tick density, the label-vs-legend fallback and the NOW rule, and nothing new to learn
 * between the two screens.
 *
 * WHAT IT DELIBERATELY DOES NOT HAVE, and each absence is a fact about a preview rather than a
 * feature left out:
 *
 *   - **No read.** The view is a prop, computed by `@/lib/coverageProjection` from what is on the
 *     form. There is nothing to fetch, so there is no in-flight latch, no timeout and no retry.
 *   - **No staleness.** `MissionCoverageTimeline` needs a 60 s ceiling because every NOW on it is
 *     the server's and nothing advances it. Here the view is recomputed from the form on every
 *     change, so it cannot be stale - and a STALE chip on a chart that is by construction current
 *     would be noise that teaches an operator to ignore the real one.
 *   - **No socket.** Nothing is running yet to publish a frame about.
 *
 * WHAT IT HAS THAT THE LIVE ONE DOES NOT: the estimate label. The server planner knows observed
 * drain, calibrated cruise speed, bay hold and dock contention; this projection knows the first
 * two and neither of the last two. That divergence is the risk the client-first decision accepted,
 * and labelling it on the chart itself - not only in prose beside it - is half the mitigation.
 */
export function CoverageTimelinePreview({ view, summary }: {
  view: CoverageTimelineView;
  /**
   * The one sentence a screen reader gets instead of the chart. Supplied by the caller rather than
   * derived here, because the sentence that matters before an attach is the VERDICT ("94% of the
   * window, longest gap 6m") and this component has the picture, not the arithmetic.
   */
  summary: string;
}) {
  const timezone = useOrgTimezone();
  const uid = useId();
  const [fullscreen, setFullscreen] = useState(false);
  const domain = timelineDomain(view);
  const hasChart = domain !== null && view.lanes.length > 0;

  /**
   * A modal must never be left holding nothing - the same rule the live chart follows, reached by
   * a different door. Here the view changes because the OPERATOR changed the form: deselecting the
   * last aircraft while the expanded chart is open empties it, and the Expand button that opened it
   * unmounts with the chart, so radix has no element to restore focus to.
   *
   * Adjusted DURING RENDER rather than in an effect, which is React's own recommendation for state
   * that derives from a prop. In an effect this is a cascading render on every recompute, and the
   * form recomputes on every keystroke.
   */
  if (fullscreen && !hasChart) setFullscreen(false);

  const headingId = `${uid}-preview`;
  const fullscreenTitleId = `${uid}-preview-fullscreen`;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span id={headingId} className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Projected rotation
        </span>
        {/* ON THE CHART, NOT ONLY BESIDE IT. This is a browser-side model of a schedule the server
            will compute differently, and a picture this concrete is exactly the kind an operator
            quotes later. It says what it is wherever it is read from. */}
        <span className="text-[10px] uppercase tracking-wider text-status-amber-fg border border-status-amber-marker/50 rounded px-1.5 py-0.5">
          Estimate
        </span>
        {hasChart && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            aria-label="Expand projected rotation to fullscreen"
            aria-haspopup="dialog"
            onClick={() => setFullscreen(true)}
          >
            <Maximize2 className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
            Expand
          </Button>
        )}
      </div>

      {/* FULLSCREEN IS NOT A THINNER VIEW - the live chart's rule, and the reason is the same here.
          Radix aria-hides the page behind the modal, so the legend that decodes the blocks and the
          sentence that summarises them have to be inside it or they are gone for a screen reader
          as well as behind the overlay. The estimate label goes with them. */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="w-[96vw] max-w-[96vw] sm:max-w-[96vw] max-h-[92vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-sm uppercase tracking-wider">
              Projected rotation
            </DialogTitle>
            <DialogDescription className="text-xs" aria-live="polite">{summary}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span id={fullscreenTitleId} className="sr-only">Projected rotation</span>
            <span className="text-[10px] uppercase tracking-wider text-status-amber-fg border border-status-amber-marker/50 rounded px-1.5 py-0.5">
              Estimate
            </span>
          </div>
          {hasChart && (
            <>
              <TimelineLegend />
              <TimelineChart view={view} domain={domain} timezone={timezone} expanded />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* The sentence an operator gets without seeing the chart, and the one a screen reader gets
          instead of it. `aria-live` deliberately absent: this recomputes on every keystroke, and a
          region that announced each intermediate plan would talk over the operator typing. */}
      <p className="text-[11px] text-muted-foreground" aria-describedby={headingId}>{summary}</p>

      {hasChart ? (
        <>
          <TimelineLegend />
          <TimelineChart view={view} domain={domain} timezone={timezone} />
        </>
      ) : (
        // NOT an empty box. A blank where a chart belongs reads as a rendering fault, and the
        // caller's `summary` is where the reason lives.
        <p className="text-[11px] text-muted-foreground">
          Nothing to draw yet - pick the aircraft that will fly this rotation.
        </p>
      )}
    </div>
  );
}
